/**
 * Shared types, API, and helper functions for manual readings.
 * Used by both useManualReadingsQueries.ts and useReadingMutations.ts.
 */

import { API } from "common/utils";
import { EventType } from "./manualInputTypes";
import type { BulkResponse, ManualDMInputResponse } from "./manualInputTypes";

export { EventType } from "./manualInputTypes";
export type {
  EventResponse,
  InputKind,
  InputVerificationStatus,
  ManualDMInputResponse,
  ManualDMInputResponseItem,
} from "./manualInputTypes";

export interface FieldMutationEffect {
  fieldGuid: string;
  biomassRecomputeQueued: boolean;
  tilesInvalidated: boolean;
}

export interface ReadingMutationResult {
  guid?: string;
  guids?: string[];
  affectedFields?: FieldMutationEffect[];
}

export const weekMs = 7 * 24 * 3600 * 1000;

class ManualInputAPI extends API<ManualDMInputResponse> {
  async getManyBulk(
    ids: Array<string>,
    includeNullDryMatter = true
  ): Promise<Array<ManualDMInputResponse>> {
    const iterate = async (nextCursor?: string) => {
      const res = await fetch(
        `${this.baseUrl}${this.entity}/bulk?guids=${ids.join(
          ","
        )}&include_null_dry_matter=${String(
          includeNullDryMatter
        )}&next_cursor=${nextCursor}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
            "Content-Type": "application/json",
          },
        }
      );

      return (await res.json()) as BulkResponse;
    };

    const json = await iterate();
    const data = json.data;

    let nextCursor = json.meta?.nextCursor;
    while (nextCursor) {
      const json = await iterate(nextCursor);
      nextCursor = json.meta?.nextCursor;
      data.push(...json.data);
    }

    return data;
  }
}

export const manualInputApi = new ManualInputAPI("manualDryMatterInput", "v2");
export const manualInputMutationApi = new API<ManualDMInputResponse>(
  "manualDryMatterInput",
  "v4"
);
// v1 API needed for PUT/DELETE — v2 only supports GET/POST
export const manualInputApiV1 = new API<ManualDMInputResponse>(
  "manualDryMatterInput",
  "v1"
);

/**
 * Process reading response from API (multiply values by 10000 for display).
 */
export function processReadingResponse(
  manualInput: ManualDMInputResponse
): ManualDMInputResponse {
  return {
    guid: manualInput.guid,
    manualDryMatterInput: {
      ...manualInput.manualDryMatterInput,
      dryMatterValue:
        manualInput.manualDryMatterInput.dryMatterValue &&
        manualInput.manualDryMatterInput.dryMatterValue * 10000,
      event: {
        ...manualInput.manualDryMatterInput.event,
        postEventCover:
          manualInput.manualDryMatterInput.event.postEventCover &&
          manualInput.manualDryMatterInput.event.postEventCover * 10000,
        preEventCover:
          manualInput.manualDryMatterInput.event.preEventCover &&
          manualInput.manualDryMatterInput.event.preEventCover * 10000,
      },
    },
  };
}

/**
 * Prepare reading for API (divide values by 10000 for storage).
 * Opposite of processReadingResponse.
 */
export function prepareReadingForApi(
  manualInput: ManualDMInputResponse
): ManualDMInputResponse {
  const event = manualInput.manualDryMatterInput.event;
  return {
    guid: manualInput.guid,
    manualDryMatterInput: {
      dryMatterValue: manualInput.manualDryMatterInput.dryMatterValue
        ? manualInput.manualDryMatterInput.dryMatterValue / 10000
        : manualInput.manualDryMatterInput.dryMatterValue,
      field: manualInput.manualDryMatterInput.field,
      dateTimeMeasurementStart:
        manualInput.manualDryMatterInput.dateTimeMeasurementStart,
      dateTimeMeasurementEnd:
        manualInput.manualDryMatterInput.dateTimeMeasurementEnd,
      kind: manualInput.manualDryMatterInput.kind,
      verificationStatus: manualInput.manualDryMatterInput.verificationStatus,
      version: manualInput.manualDryMatterInput.version,
      event: {
        eventType: event.eventType,
        dateTimeIn: event.dateTimeIn,
        dateTimeOut: event.dateTimeOut,
        dateTimeCut: event.dateTimeCut,
        preEventCover: event.preEventCover
          ? event.preEventCover / 10000
          : event.preEventCover,
        postEventCover: event.postEventCover
          ? event.postEventCover / 10000
          : event.postEventCover,
        animalGroupGuid: event.animalGroupGuid,
      },
    },
  };
}

/**
 * Shared query function to fetch readings for a field.
 */
export async function fetchReadingsForFieldFn(
  fieldId: string,
  includedKinds: string = "user",
  includeAnimalGroups: boolean = false
): Promise<ManualDMInputResponse[]> {
  const params = new URLSearchParams({
    field: fieldId,
    included_kinds: includedKinds,
    include_null_dry_matter: "true",
  });
  const includesMachine = includedKinds
    .split(",")
    .map((kind) => kind.trim())
    .includes("machine");
  if (includesMachine) {
    params.set("exclude_machine_daily", "true");
  }
  if (includeAnimalGroups) {
    params.set("include_animal_groups", "true");
  }
  const res = await fetch(
    `${
      import.meta.env.VITE_API_ENDPOINT
    }/v3/manualDryMatterInput/fieldEvents?${params}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    }
  );
  if (!res.ok)
    throw await res.json().catch(() => ({ message: res.statusText }));
  const readings = (await res.json()) as ManualDMInputResponse[];
  return readings.map(processReadingResponse);
}

/**
 * Check if an event is valid (has required dates).
 */
export function isValidEvent(reading: ManualDMInputResponse): boolean {
  switch (reading?.manualDryMatterInput?.event?.eventType) {
    case EventType.GrazingEvent:
      return (
        !!reading?.manualDryMatterInput?.event?.dateTimeIn ||
        !!reading?.manualDryMatterInput?.event?.dateTimeOut
      );
    case EventType.PartialGrazingEvent:
      return (
        !!reading?.manualDryMatterInput?.event?.dateTimeIn &&
        !!reading?.manualDryMatterInput?.event?.dateTimeOut
      );
    case EventType.SilageEvent:
      return !!reading?.manualDryMatterInput?.event?.dateTimeCut;
  }
  return true;
}

/**
 * Get the latest date from a reading (for sorting).
 */
export function getLatestCurrentDate(
  manualDMInputResponse: ManualDMInputResponse
): Date | undefined {
  if (manualDMInputResponse.manualDryMatterInput.event) {
    if (
      manualDMInputResponse.manualDryMatterInput.event.eventType ===
        EventType.GrazingEvent ||
      manualDMInputResponse.manualDryMatterInput.event.eventType ===
        EventType.PartialGrazingEvent
    ) {
      if (manualDMInputResponse.manualDryMatterInput.event.dateTimeOut) {
        return new Date(
          manualDMInputResponse.manualDryMatterInput.event.dateTimeOut
        );
      }
      if (manualDMInputResponse.manualDryMatterInput.event.dateTimeIn) {
        return new Date(
          manualDMInputResponse.manualDryMatterInput.event.dateTimeIn
        );
      }
    }
  }
  return;
}
